import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import api, { errorStatus } from "../api/account";
import "./Account.css";
import "./Unsubscribe.css";

/** The Pocket Ledger app icon, served from public/ alongside the favicons. The
 *  same round mark an emailed PIN-reset link lands on (ResetPin.tsx), so the
 *  two mail-driven pages carry the same product logo. */
const APP_ICON = "/img/pl-app-icon.png";

/** The E8 company mark. E8 is who is writing and Pocket Ledger is what the mail
 *  is about, so the company mark signs the foot of the card rather than
 *  standing in the masthead with the product icon. */
const E8_MARK = "/img/e8-mark.png";

/**
 * Anonymous unsubscribe page — https://pl.eeighttech.com/account/unsubscribe
 *
 * This is the destination of the unsubscribe link in the footer of campaign
 * mail. That link is a single hardcoded URL, not a per-recipient token (see the
 * ses-templates README), so the page cannot know who arrived and has to ask for
 * the address.
 *
 * It is deliberately its own route rather than part of the signed-in account:
 * the reader is not signed in, has no credentials, and must not have to create
 * any to stop the mail. Nothing here reads the auth context.
 *
 * What it stops and what it does not: the address comes off the campaign
 * (feature-announcement) list. Transactional mail — receipts, PIN resets,
 * support replies — is not list mail and keeps being sent, which the success
 * state says out loud so the result is not a surprise later.
 */
export default function Unsubscribe() {
	const [email, setEmail] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState("");
	const [done, setDone] = useState(false);

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (submitting) return;

		const address = email.trim();
		// Deliberately loose. The service uses the same parser the sending paths
		// use, and it is the one that decides what an address is — a stricter
		// rule here would refuse to let someone off a list over a formality the
		// sender would have accepted. This only catches the obviously-empty.
		if (!address || address.indexOf("@") < 1) {
			setError("Please enter the email address you want to unsubscribe.");
			return;
		}

		setError("");
		setSubmitting(true);

		try {
			await api.account.unsubscribeFromEmails(address);
			setDone(true);
		} catch (err) {
			// 400 is the service rejecting the address; everything else (a 502
			// from SES, a network failure) is us failing, and must not be shown
			// as though the address were off the list — it would not be.
			setError(
				errorStatus(err) === 400
					? "That doesn't look like a valid email address. Please check it and try again."
					: "We couldn't record your unsubscribe just now. Please try again in a moment."
			);
		}

		setSubmitting(false);
	};

	return (
		<div className="account-route unsubscribe-route">
			<div className="account-page account-page--centered">
				<div className="signin-card">
					<div className="signin-brand unsubscribe-brand">
						<img src={APP_ICON} alt="Pocket Ledger" />
					</div>
					<p className="unsubscribe-product">Pocket Ledger</p>

					{done ? (
						<UnsubscribeDone address={email.trim()} />
					) : (
						<>
							<h1 className="signin-title">Unsubscribe</h1>
							<p className="signin-subtitle">
								Enter your email address and we'll take you off Pocket Ledger
								announcements.
							</p>

							<form className="signin-form" onSubmit={handleSubmit}>
								<div className="signin-field">
									<label htmlFor="unsubscribe-email">Email address</label>
									<input
										id="unsubscribe-email"
										type="email"
										name="email"
										autoComplete="email"
										placeholder="you@example.com"
										value={email}
										onChange={(e) => {
											setEmail(e.target.value);
											if (error) setError("");
										}}
										disabled={submitting}
									/>
								</div>

								{error && <div className="signin-error">{error}</div>}

								<button className="signin-btn" type="submit" disabled={submitting}>
									{submitting ? "Unsubscribing…" : "Unsubscribe"}
								</button>
							</form>

							<p className="signin-hint">
								Still want the mail? <Link to="/">Back to Pocket Ledger</Link>
							</p>
						</>
					)}

					{/* Signs the card in both states, so the company behind the mail
					    is on screen whether or not the reader has acted yet. alt is
					    empty because the line beside it already says "E8". */}
					<p className="unsubscribe-e8">
						<img src={E8_MARK} alt="" />
						<span>An E8 product</span>
					</p>
				</div>
			</div>
		</div>
	);
}

// ── Success ─────────────────────────────────────────────────────────────────

/**
 * Echoes the address back so the reader can see that what they typed is what
 * was suppressed — the one thing a mistyped opt-out has to make obvious, since
 * a typo here silently leaves them on the list AND suppresses a stranger.
 */
function UnsubscribeDone({ address }: { address: string }) {
	return (
		<div className="unsubscribe-done">
			<div className="unsubscribe-done-icon">✓</div>
			<h1 className="unsubscribe-done-title">You're unsubscribed</h1>
			<p className="unsubscribe-done-text">
				<b>{address}</b> has been removed from Pocket Ledger announcements.
			</p>
			<p className="unsubscribe-done-note">
				Receipts, purchase confirmations, PIN resets and replies from support
				are not announcements, so they'll still reach you.
			</p>
			<Link className="unsubscribe-done-btn" to="/">
				Back to Pocket Ledger
			</Link>
		</div>
	);
}
